import { useState } from 'react'
import { format, subMonths } from 'date-fns'
import {
	ArrowDownRight,
	ArrowUpRight,
	TrendingUp,
	Calendar,
	DollarSign,
	Receipt,
} from 'lucide-react'

import { Card, CardContent, CardHeader, CardTitle } from '@/common/ui/card'
import { TableCell } from '@/common/ui/table'
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from '@/common/ui/select'
import { DataTable } from '@/common/ui/table/data-table'

import useQueryGetBillingDetails from '../../api/hooks/useQueryGetBillingDetails'
import useQueryGetGeneralSetting from '@/modules/client-setting/api/hooks/useQueryGetGeneralSetting'
import { promotionColumns, formatBillingMonth } from './constants'
import ProviderBreakdownTable from './ProviderBreakdownTable'
import type { PromotionBillingBreakdown } from '../../type'

// Generate available months (last 12 months)
const generateAvailableMonths = (): string[] => {
	const months: string[] = []
	const now = new Date()
	for (let i = 0; i < 12; i++) {
		const date = subMonths(now, i)
		months.push(format(date, 'yyyy-MM'))
	}
	return months
}

const AVAILABLE_MONTHS = generateAvailableMonths()

export default function BillingDetailsSummary() {
	const [selectedMonth, setSelectedMonth] = useState<string>(
		AVAILABLE_MONTHS[0],
	)

	// Fetch billing details from API
	const { data: billingData, isLoading } = useQueryGetBillingDetails({
		billingMonth: selectedMonth,
	})

	// Fetch general settings for monthly platform fee (fallback)
	const { data: generalSettings } = useQueryGetGeneralSetting()
	const monthlyPlatformFee =
		billingData?.monthlyPlatformFee ?? generalSettings?.monthlyPlatformFee ?? 0

	// Calculate the totals separately
	const creditChargeThb = billingData?.totalChargeThb || 0
	const creditChargeUsd = billingData?.totalChargeUsd || 0
	const exchangeRate = billingData?.exchangeRate || 0

	const formatCurrency = (value: number): string => {
		return `฿${value.toLocaleString('th-TH', {
			minimumFractionDigits: 2,
			maximumFractionDigits: 2,
		})}`
	}

	const formatTHB = (value: number): string => {
		return `฿${value.toLocaleString('th-TH', {
			minimumFractionDigits: 2,
			maximumFractionDigits: 2,
		})}`
	}

	const formatUSD = (value: number): string => {
		return `$${value.toLocaleString('en-US', {
			minimumFractionDigits: 2,
			maximumFractionDigits: 2,
		})}`
	}

	const summaryItems = [
		{
			title: 'อัตราแลกเปลี่ยน (USD/THB)',
			value: exchangeRate > 0 ? `฿${exchangeRate.toFixed(2)}` : 'ไม่ระบุ',
			icon: DollarSign,
			color: 'text-purple-600',
			bgColor: 'bg-purple-50 dark:bg-purple-950/30',
		},
		{
			title: 'ค่าใช้งานรายเดือน',
			value: formatUSD(monthlyPlatformFee),
			icon: DollarSign,
			color: 'text-blue-600',
			bgColor: 'bg-blue-50 dark:bg-blue-950/30',
		},
		{
			title: 'ยอดโอนเข้า',
			value: billingData ? formatCurrency(billingData.totalTransferIn) : '-',
			icon: ArrowDownRight,
			color: 'text-green-600',
			bgColor: 'bg-green-50 dark:bg-green-950/30',
		},
		{
			title: 'ยอดโอนออก',
			value: billingData ? formatCurrency(billingData.totalTransferOut) : '-',
			icon: ArrowUpRight,
			color: 'text-red-600',
			bgColor: 'bg-red-50 dark:bg-red-950/30',
		},
		{
			title: 'กินลูกค้า',
			value: billingData ? formatCurrency(billingData.totalProfit) : '-',
			icon: TrendingUp,
			color: 'text-emerald-600',
			bgColor: 'bg-emerald-50 dark:bg-emerald-950/30',
		},
	]

	const providerFooter = billingData?.providerBreakdown?.length ? (
		<>
			<TableCell />
			<TableCell className="font-bold">รวมทั้งหมด</TableCell>
			<TableCell />
			<TableCell className="text-right font-bold text-green-600">
				{formatCurrency(billingData.totalTransferIn)}
			</TableCell>
			<TableCell className="text-right font-bold text-red-600">
				{formatCurrency(billingData.totalTransferOut)}
			</TableCell>
			<TableCell className="text-right font-bold text-emerald-600">
				{formatCurrency(billingData.totalProfit)}
			</TableCell>
			<TableCell className="text-right font-bold text-purple-600">
				{formatCurrency(
					billingData.providerBreakdown.reduce(
						(sum, provider) => sum + provider.nonPromotionCharge,
						0,
					),
				)}
			</TableCell>
			<TableCell className="text-right font-bold text-orange-600">
				{formatCurrency(
					billingData.providerBreakdown.reduce(
						(sum, provider) => sum + provider.promotionCharge,
						0,
					),
				)}
			</TableCell>
			<TableCell className="text-right font-bold text-blue-600">
				{formatTHB(billingData.totalChargeThb)}
			</TableCell>
		</>
	) : null

	const promotionFooter = billingData?.promotionBreakdown?.length ? (
		<>
			<TableCell className="font-bold">รวมโปรโมชั่น</TableCell>
			<TableCell />
			<TableCell className="text-right font-bold text-green-600">
				{formatCurrency(billingData.totalPromotionTransferIn || 0)}
			</TableCell>
			<TableCell className="text-right font-bold text-red-600">
				{formatCurrency(billingData.totalPromotionTransferOut || 0)}
			</TableCell>
			<TableCell className="text-right font-bold text-emerald-600">
				{formatCurrency(billingData.totalPromotionProfit || 0)}
			</TableCell>
			<TableCell className="text-right font-bold text-orange-600">
				{formatCurrency(billingData.totalPromotionCharge || 0)}
			</TableCell>
		</>
	) : null

	return (
		<div className="space-y-4">
			{/* Summary Header with Month Filter */}
			<Card>
				<CardHeader>
					<div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
						<CardTitle>สรุปการเรียกเก็บเงิน</CardTitle>
						<div className="flex items-center gap-2">
							<Calendar className="h-4 w-4 text-muted-foreground" />
							<Select value={selectedMonth} onValueChange={setSelectedMonth}>
								<SelectTrigger className="w-[180px]">
									<SelectValue placeholder="เลือกเดือน" />
								</SelectTrigger>
								<SelectContent>
									{AVAILABLE_MONTHS.map((month) => (
										<SelectItem key={month} value={month}>
											{formatBillingMonth(month)}
										</SelectItem>
									))}
								</SelectContent>
							</Select>
						</div>
					</div>
				</CardHeader>
				<CardContent>
					{/* Credit and Monthly Fee Display */}
					<div className="mb-6 flex flex-wrap gap-3">
						{/* THB Card */}
						<div className="inline-flex items-center gap-3 rounded-lg border border-blue-200 bg-blue-50 px-4 py-3 dark:border-blue-800 dark:bg-blue-950/30">
							<div className="rounded-lg bg-blue-500 p-2">
								<Receipt className="h-5 w-5 text-white" />
							</div>
							<div>
								<p className="text-xs text-muted-foreground">
									ยอดเรียกเก็บเครดิต (THB)
								</p>
								<p className="text-lg font-semibold text-blue-600">
									{billingData ? formatTHB(creditChargeThb) : '-'}
								</p>
							</div>
						</div>

						{/* USD Card */}
						<div className="inline-flex items-center gap-3 rounded-lg border border-blue-200 bg-blue-50 px-4 py-3 dark:border-blue-800 dark:bg-blue-950/30">
							<div className="rounded-lg bg-blue-500 p-2">
								<Receipt className="h-5 w-5 text-white" />
							</div>
							<div>
								<p className="text-xs text-muted-foreground">
									ยอดเรียกเก็บเครดิต (USD)
								</p>
								<p className="text-lg font-semibold text-blue-600">
									{billingData ? formatUSD(creditChargeUsd) : '-'}
								</p>
							</div>
						</div>

						{/* Monthly Platform Fee */}
						<div className="inline-flex items-center gap-3 rounded-lg border-2 border-orange-400 bg-orange-50 px-4 py-3 dark:bg-orange-950/30">
							<div className="rounded-lg bg-orange-500 p-2">
								<Receipt className="h-5 w-5 text-white" />
							</div>
							<div>
								<p className="text-xs text-muted-foreground">ค่ารายเดือน</p>
								<p className="text-lg font-semibold text-orange-600">
									{formatUSD(monthlyPlatformFee)}
								</p>
							</div>
						</div>
					</div>

					{/* Summary Stats */}
					<div className="mb-6 grid grid-cols-3 gap-4">
						{summaryItems.map((item) => {
							const Icon = item.icon
							return (
								<div
									key={item.title}
									className="flex items-center gap-3 rounded-lg border p-4"
								>
									<div className={`rounded-lg p-2 ${item.bgColor}`}>
										<Icon className={`h-5 w-5 ${item.color}`} />
									</div>
									<div>
										<p className="text-sm text-muted-foreground">
											{item.title}
										</p>
										<p className={`text-lg font-bold ${item.color}`}>
											{item.value}
										</p>
									</div>
								</div>
							)
						})}
					</div>

					{/* Promotion Breakdown Table */}
					<div className="mb-6">
						<div className="mb-3">
							<h4 className="text-sm font-medium">รายละเอียดแบบโปรโมชั่น</h4>
							<p className="text-xs text-muted-foreground">
								รายการที่ใช้โปรโมชั่นจะได้รับส่วนลดค่าบริการ
							</p>
						</div>
						<div className="mb-3 rounded-md bg-amber-50 p-3 text-sm text-amber-800 dark:bg-amber-950/30 dark:text-amber-400">
							⚠️ อัตรา
							%ถือของโปรโมชั่นหรือเครดิตฟรีจะถูกใช้คำนวณเฉพาะรายการจากค่าย
							<span className="font-semibold">PGHARD</span> และ{' '}
							<span className="font-semibold">JOKERHARD</span> เท่านั้น
							ค่ายอื่นจะคิดที่อัตราคอมพานีปกติแม้จะมีการใช้โปรโมชั่น
						</div>
						<DataTable
							columns={promotionColumns}
							data={
								(billingData?.promotionBreakdown ??
									[]) as PromotionBillingBreakdown[]
							}
							isLoading={isLoading}
							footer={promotionFooter}
							pagination
						/>
					</div>

					{/* Provider Breakdown Table */}
					<div>
						<div className="mb-3">
							<h4 className="text-sm font-medium">
								รายละเอียดตามผู้ให้บริการ (รวมทุกรายการ)
							</h4>
							<p className="text-xs text-muted-foreground">
								ยอดรวมทั้งหมดรวมโปรโมชั่นและไม่รวมโปรโมชั่น
								(คลิกลูกศรเพื่อดูรายละเอียดโปรโมชั่น)
							</p>
						</div>
						<ProviderBreakdownTable
							data={billingData?.providerBreakdown ?? []}
							isLoading={isLoading}
							footer={providerFooter}
						/>
						<div className="mt-3 rounded-md bg-amber-50 p-3 text-sm text-amber-800 dark:bg-amber-950/30 dark:text-amber-400">
							💡 ยอดเรียกเก็บอาจคาดเคลื่อนจาก (กินลูกค้า × อัตราคอมพานี)
							เนื่องจากบางรายการได้รับส่วนลดจากโปรโมชั่น
						</div>
					</div>
				</CardContent>
			</Card>
		</div>
	)
}
